/**
 * PauseMenu - Game pause menu with settings
 * Triggered by ESC key, provides access to settings and quit functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { soundManager } from '../systems/SoundManager';
import { useRadio } from './ui/RadioPlayer';
import {
  Volume2,
  VolumeX,
  Music,
  Monitor,
  Maximize,
  Minimize,
  LogOut,
  Home,
  X,
  Settings,
  Globe,
  Save,
  FolderOpen,
  Check,
  AlertCircle,
  User
} from 'lucide-react';
import { saveGame, loadGame, saveExists } from '../lib/saveManager';
import { useWorldState } from './state/WorldStateContext';
import { useAuth } from './auth/AuthContext';
import AuthModal from './auth/AuthModal';
import { debug } from '../lib/debug.js';

// Web version — no Electron

export default function PauseMenu({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isPlaying: musicPlaying, toggle: toggleMusic } = useRadio();
  const { worldState, setWorldState } = useWorldState();
  const { user, signOut: authSignOut } = useAuth();

  const [activeTab, setActiveTab] = useState('main'); // main, audio, display, language, account
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sfxVolume, setSfxVolume] = useState(() => soundManager.getVolume() * 100);
  const [sfxMuted, setSfxMuted] = useState(() => soundManager.isMuted());
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
  const [currentLanguage, setCurrentLanguage] = useState(() => i18n.language || 'en');
  const [currentResolution, setCurrentResolution] = useState(() => {
    const saved = localStorage.getItem('gameResolution');
    return saved || 'fullscreen';
  });
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'saved', 'error'
  const [loadStatus, setLoadStatus] = useState(null); // null, 'loading', 'loaded', 'error'

  // Update fullscreen state when it changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle ESC to close menu or go back to main tab
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (activeTab !== 'main') {
          setActiveTab('main');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeTab, onClose]);

  // SFX Volume handler
  const handleSfxVolumeChange = useCallback((value) => {
    setSfxVolume(value);
    soundManager.setVolume(value / 100);
  }, []);

  // SFX Mute toggle
  const handleSfxMuteToggle = useCallback(() => {
    const newMuted = soundManager.toggleMute();
    setSfxMuted(newMuted);
  }, []);

  // Fullscreen toggle
  const handleFullscreenToggle = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (e) {
      console.warn('Fullscreen toggle failed:', e);
    }
  }, []);

  // Resolution change (works in Electron by resizing window)
  const handleResolutionChange = useCallback((resolution) => {
    setCurrentResolution(resolution);
    localStorage.setItem('gameResolution', resolution);

    if (resolution === 'fullscreen') {
      // Go fullscreen
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } else {
      // Exit fullscreen first if needed
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      // Web: resolution buttons just toggle fullscreen on/off
      // No window resize in browser
    }
  }, []);

  // Language change
  const handleLanguageChange = useCallback((lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  }, [i18n]);

  // Return to main menu
  const handleMainMenu = useCallback(() => {
    onClose();
    navigate('/MainMenu');
  }, [navigate, onClose]);

  // Quit game — web version goes to main menu
  const handleQuitGame = useCallback(() => {
    handleMainMenu();
  }, [handleMainMenu]);

  // Manual save game
  const handleManualSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      const result = await saveGame(worldState);
      if (result.success) {
        debug('[PauseMenu] Manual save successful:', result.path);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } else {
        // Save reported failure — but verify if it actually saved before showing error
        // robustSaveManager has multiple fallbacks, so the data is likely safe
        const exists = await saveExists();
        if (exists) {
          debug('[PauseMenu] Save reported failure but data exists on disk — showing success');
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus(null), 2000);
        } else {
          console.error('[PauseMenu] Manual save failed:', result.error);
          setSaveStatus('error');
          setTimeout(() => setSaveStatus(null), 3000);
        }
      }
    } catch (err) {
      // Same fallback — verify before showing error
      const exists = await saveExists().catch(() => false);
      if (exists) {
        debug('[PauseMenu] Save threw but data exists on disk — showing success');
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } else {
        console.error('[PauseMenu] Manual save error:', err);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }
  }, [worldState]);

  // Manual load game
  const handleManualLoad = useCallback(async () => {
    setLoadStatus('loading');
    try {
      const result = await loadGame();
      if (result.success && result.exists && result.data) {
        debug('[PauseMenu] Manual load successful');
        setWorldState(prev => ({
          ...prev,
          ...result.data,
          gameSpeed: 1,
          isPaused: true, // Stay paused after load
        }));
        setLoadStatus('loaded');
        setTimeout(() => setLoadStatus(null), 2000);
      } else if (!result.exists) {
        debug('[PauseMenu] No save file found');
        setLoadStatus('error');
        setTimeout(() => setLoadStatus(null), 3000);
      } else {
        console.error('[PauseMenu] Manual load failed:', result.error);
        setLoadStatus('error');
        setTimeout(() => setLoadStatus(null), 3000);
      }
    } catch (err) {
      console.error('[PauseMenu] Manual load error:', err);
      setLoadStatus('error');
      setTimeout(() => setLoadStatus(null), 3000);
    }
  }, [setWorldState]);

  if (!isOpen) return null;

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#111',
          border: '1px solid #333',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #222',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            {activeTab === 'main' && (
              <>
                <span style={{ color: '#22c55e' }}>■</span> PAUSED
              </>
            )}
            {activeTab === 'audio' && (
              <>
                <Volume2 size={24} style={{ color: '#22c55e' }} /> Audio
              </>
            )}
            {activeTab === 'display' && (
              <>
                <Monitor size={24} style={{ color: '#22c55e' }} /> Display
              </>
            )}
            {activeTab === 'language' && (
              <>
                <Globe size={24} style={{ color: '#22c55e' }} /> Language
              </>
            )}
            {activeTab === 'account' && (
              <>
                <User size={24} style={{ color: '#22c55e' }} /> Account
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => e.target.style.color = '#fff'}
            onMouseLeave={(e) => e.target.style.color = '#666'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 24px', overflowY: 'auto', maxHeight: 'calc(80vh - 80px)' }}>

          {/* Main Menu */}
          {activeTab === 'main' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <MenuButton onClick={onClose} primary>
                Resume Game
              </MenuButton>

              <div style={{ height: '8px' }} />

              <MenuButton onClick={() => setActiveTab('audio')} icon={<Volume2 size={20} />}>
                Audio Settings
              </MenuButton>

              <MenuButton onClick={() => setActiveTab('display')} icon={<Monitor size={20} />}>
                Display Settings
              </MenuButton>

              <MenuButton onClick={() => setActiveTab('language')} icon={<Globe size={20} />}>
                Language
              </MenuButton>

              <MenuButton onClick={() => setActiveTab('account')} icon={<User size={20} />}>
                Account
              </MenuButton>

              <div style={{ height: '8px' }} />

              {/* Save/Load Section */}
              <MenuButton
                onClick={handleManualSave}
                icon={saveStatus === 'saving' ? (
                  <div className="animate-spin" style={{ width: 20, height: 20, border: '2px solid #22c55e', borderTopColor: 'transparent', borderRadius: '50%' }} />
                ) : saveStatus === 'saved' ? (
                  <Check size={20} style={{ color: '#22c55e' }} />
                ) : saveStatus === 'error' ? (
                  <AlertCircle size={20} style={{ color: '#ef4444' }} />
                ) : (
                  <Save size={20} />
                )}
              >
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Save Failed' : 'Save Game'}
              </MenuButton>

              <MenuButton
                onClick={handleManualLoad}
                icon={loadStatus === 'loading' ? (
                  <div className="animate-spin" style={{ width: 20, height: 20, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%' }} />
                ) : loadStatus === 'loaded' ? (
                  <Check size={20} style={{ color: '#22c55e' }} />
                ) : loadStatus === 'error' ? (
                  <AlertCircle size={20} style={{ color: '#ef4444' }} />
                ) : (
                  <FolderOpen size={20} />
                )}
              >
                {loadStatus === 'loading' ? 'Loading...' : loadStatus === 'loaded' ? 'Loaded!' : loadStatus === 'error' ? 'Load Failed' : 'Load Game'}
              </MenuButton>

              <div style={{ height: '8px' }} />

              <MenuButton onClick={handleMainMenu} icon={<Home size={20} />} warning>
                Main Menu
              </MenuButton>

              <MenuButton onClick={handleQuitGame} icon={<LogOut size={20} />} danger>
                Quit Game
              </MenuButton>

              <div style={{ height: '8px' }} />

              <a
                href="https://discord.gg/SVkBsn688z"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '8px',
                  color: '#666',
                  fontSize: '13px',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#5865F2'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                Discord Community
              </a>
            </div>
          )}

          {/* Audio Settings */}
          {activeTab === 'audio' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <button
                onClick={() => setActiveTab('main')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              >
                ← Back
              </button>

              {/* SFX Volume */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>
                    Sound Effects
                  </label>
                  <button
                    onClick={handleSfxMuteToggle}
                    style={{
                      background: sfxMuted ? '#ef4444' : '#22c55e',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    {sfxMuted ? 'MUTED' : 'ON'}
                  </button>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sfxVolume}
                  onChange={(e) => handleSfxVolumeChange(Number(e.target.value))}
                  disabled={sfxMuted}
                  style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '4px',
                    background: sfxMuted ? '#333' : `linear-gradient(to right, #22c55e ${sfxVolume}%, #333 ${sfxVolume}%)`,
                    appearance: 'none',
                    cursor: sfxMuted ? 'not-allowed' : 'pointer',
                    opacity: sfxMuted ? 0.5 : 1,
                  }}
                />
                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                  {Math.round(sfxVolume)}%
                </div>
              </div>

              {/* Music Volume */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>
                    Background Music
                  </label>
                  <button
                    onClick={toggleMusic}
                    style={{
                      background: musicPlaying ? '#22c55e' : '#ef4444',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    {musicPlaying ? 'ON' : 'OFF'}
                  </button>
                </div>
                <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>
                  Toggle the ambient radio music on/off
                </p>
              </div>
            </div>
          )}

          {/* Display Settings */}
          {activeTab === 'display' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <button
                onClick={() => setActiveTab('main')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              >
                ← Back
              </button>

              {/* Resolution Selection */}
              <div>
                <label style={{ color: '#fff', fontSize: '16px', fontWeight: '500', display: 'block', marginBottom: '12px' }}>
                  Resolution
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    { value: 'fullscreen', label: 'Fullscreen', desc: 'Native' },
                    { value: '2560x1440', label: '2560 × 1440', desc: '1440p' },
                    { value: '1920x1080', label: '1920 × 1080', desc: '1080p' },
                    { value: '1600x900', label: '1600 × 900', desc: '900p' },
                    { value: '1366x768', label: '1366 × 768', desc: 'HD' },
                    { value: '1280x720', label: '1280 × 720', desc: '720p' },
                  ].map((res) => (
                    <button
                      key={res.value}
                      onClick={() => handleResolutionChange(res.value)}
                      style={{
                        background: currentResolution === res.value ? '#22c55e20' : '#1a1a1a',
                        border: `2px solid ${currentResolution === res.value ? '#22c55e' : '#333'}`,
                        borderRadius: '8px',
                        padding: '12px',
                        color: currentResolution === res.value ? '#22c55e' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{res.label}</div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{res.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fullscreen Toggle */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>
                    Fullscreen Mode
                  </label>
                  <button
                    onClick={handleFullscreenToggle}
                    style={{
                      background: isFullscreen ? '#22c55e' : '#333',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                    {isFullscreen ? 'Exit Fullscreen' : 'Go Fullscreen'}
                  </button>
                </div>
                <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>
                  Press F11 to toggle fullscreen anytime
                </p>
              </div>
            </div>
          )}

          {/* Language Settings */}
          {activeTab === 'language' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                onClick={() => setActiveTab('main')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              >
                ← Back
              </button>

              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  style={{
                    background: currentLanguage === lang.code ? '#22c55e20' : '#1a1a1a',
                    border: `2px solid ${currentLanguage === lang.code ? '#22c55e' : '#333'}`,
                    borderRadius: '8px',
                    padding: '16px 20px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    fontSize: '16px',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{lang.flag}</span>
                  <span style={{ fontWeight: currentLanguage === lang.code ? '600' : '400' }}>
                    {lang.name}
                  </span>
                  {currentLanguage === lang.code && (
                    <span style={{ marginLeft: 'auto', color: '#22c55e' }}>✓</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Account Settings */}
          {activeTab === 'account' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                onClick={() => setActiveTab('main')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              >
                ← Back
              </button>

              {/* Account Info */}
              <div style={{
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                padding: '16px 20px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: user?.isAnonymous ? '#333' : '#22c55e20',
                    border: `2px solid ${user?.isAnonymous ? '#555' : '#22c55e'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <User size={20} style={{ color: user?.isAnonymous ? '#888' : '#22c55e' }} />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>
                      {user?.displayName ?? 'Unknown'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: user?.isAnonymous ? '#f59e0b' : '#22c55e',
                      fontWeight: '500',
                    }}>
                      {user?.isAnonymous ? 'Guest Account' : 'Verified'}
                    </div>
                  </div>
                </div>

                {user?.email && (
                  <div style={{ color: '#666', fontSize: '13px' }}>
                    {user.email}
                  </div>
                )}
              </div>

              {/* Actions */}
              {user?.isAnonymous ? (
                <>
                  <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                    Link an account to save your progress across devices and browsers.
                  </p>
                  <MenuButton
                    onClick={() => setShowAuthModal(true)}
                    icon={<User size={20} />}
                    primary
                  >
                    Create Account
                  </MenuButton>
                </>
              ) : (
                <MenuButton
                  onClick={() => {
                    authSignOut()
                    onClose()
                  }}
                  icon={<LogOut size={20} />}
                  danger
                >
                  Sign Out
                </MenuButton>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #222',
            color: '#555',
            fontSize: '12px',
            textAlign: 'center',
          }}
        >
          Press ESC to {activeTab === 'main' ? 'resume' : 'go back'}
        </div>
      </div>

      {/* Auth Modal (for account linking) */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode="link"
      />
    </div>
  );
}

// Reusable menu button component
function MenuButton({ children, onClick, icon, primary, warning, danger }) {
  const [hovered, setHovered] = useState(false);

  let bgColor = '#1a1a1a';
  let borderColor = '#333';
  let textColor = '#fff';

  if (primary) {
    bgColor = hovered ? '#22c55e' : '#16a34a';
    borderColor = '#22c55e';
  } else if (warning) {
    bgColor = hovered ? '#f59e0b20' : '#1a1a1a';
    borderColor = hovered ? '#f59e0b' : '#333';
    textColor = '#f59e0b';
  } else if (danger) {
    bgColor = hovered ? '#ef444420' : '#1a1a1a';
    borderColor = hovered ? '#ef4444' : '#333';
    textColor = '#ef4444';
  } else if (hovered) {
    bgColor = '#222';
    borderColor = '#444';
  }

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: bgColor,
        border: `2px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '16px 20px',
        color: textColor,
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: primary ? '600' : '500',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transition: 'all 0.15s',
        textAlign: 'left',
      }}
    >
      {icon}
      {children}
    </button>
  );
}
